import { createRouter, createWebHistory } from 'vue-router'
import { getOAuthToken, isTokenValid, refreshAuthToken } from '../services/api/apiAuthHelpers.ts'
import { getContent } from '@/services/cms/util.js'
import { getSystemAvailability } from '@/services/api/systemability/systemability.js'
import { useSelectedPlanStore } from '@/stores/plans/selectedPlan.ts'
import { useAccessRightsStore } from '../stores/accessRights/accessRights.ts'
import { useClaimsFilters } from '@/stores/claims/claimsFilters'
import { routes } from './routeList.ts'
import { isKeyOfObject } from '@/utils/checkers.ts'
import { checkCarrierCode } from '@/utils/globalVars.ts'

const router = createRouter({
	history: createWebHistory(import.meta.env.BASE_URL),
	routes: routes,
	scrollBehavior() {
		return { top: 0 }
	}
})

const banList = router.options.routes.filter((route) => route?.meta?.hideUntilPlanSelected)

router.beforeEach(async (to, from) => {
	if (to.path.includes('//')) {
		const cleanPath = to.path.replace(/\/+/g, '/')
		return { path: cleanPath, query: to.query, hash: to.hash, replace: true }
	}
	if (to.path == '/bypass-outage') localStorage.setItem('bypass_outage', 'true')
	if (to.name === 'PharmacyCosts' && from.name && from.name != 'PharmacyCosts') {
		to.meta.reset = true
	}

	// Before each route we test if the system availability is down and redirect to the outage-message
	checkSystemAvailable(to.path)

	const planStore = useSelectedPlanStore().getStore()
	const rightsStore = useAccessRightsStore().getStore()
	const claimsFiltersStore = useClaimsFilters().loaded

	if (to.name === 'MedicalClaims' && !from.path.includes('claim') && from.name != undefined) {
		useClaimsFilters().$reset()
	}

	// Handle contractType Redirect
	function contractTypeRedirects() {
		const contractType = planStore?.contractType ? planStore.contractType.toLowerCase() : false

		// checks if planStore and to.meta contractTypes exist
		if (contractType && to.meta?.contractTypes) {
			// IF we need a contract type, check to make sure the url isn't already using it
			if (
				!to.path.includes(contractType) &&
				(to.meta.contractTypes as string[]).includes(contractType)
			) {
				to.path = `/${contractType}${to.path}`
				to.fullPath = `/${contractType}${to.fullPath}`
				return true
			}
		}

		return true
	}

	function hasRights(): boolean {
		let hasRight = false
		if (to.meta.rightsValue && typeof to.meta.rightsValue === 'string') {
			if (
				rightsStore &&
				isKeyOfObject(to.meta.rightsValue as string, rightsStore) &&
				rightsStore[to.meta.rightsValue]
			)
				hasRight = true
		} else if (to.meta.rightsValue) {
			to.meta.rightsValue.forEach((right) => {
				if (rightsStore && isKeyOfObject(right, rightsStore) && rightsStore[right]) hasRight = true
			})
		}
		return hasRight
	}

	// Check route vs accessRights
	function routeAccessRights() {
		// IF route HAS meta.rightsValue test meta.rightsValue against accessRights
		if (!rightsStore) return
		if (to.meta.rightsValue) {
			// Check if user has rights to view programs
			if (to.meta.rightsValue.includes('programs') && rightsStore.programs?.length) {
				return contractTypeRedirects()
			}
			// wm-pharmacy-spending
			else if (
				to.meta.rightsValue.includes('wm-pharmacy-spending') &&
				checkCarrierCode(planStore.carrierCode, 'walmart')
			) {
				return contractTypeRedirects()
			}
			// Check programs by ID
			else if (
				to.meta.rightsValue.includes('programs-id') &&
				rightsStore.programs?.find((x) => x.programId === to.params.id)
			) {
				return contractTypeRedirects()
			}
			// Check is user has payBill or medicarePayments
			else if (
				to.meta.rightsValue.includes('payment') &&
				(rightsStore?.payBill ||
					(!rightsStore?.payBill &&
						rightsStore?.pageIdentifiers?.some((x) => x.type == 'medicarePayment')))
			) {
				return contractTypeRedirects()
			}
			//embold redirect
			else if (
				to.meta.rightsValue.includes('embold') &&
				rightsStore?.findCareAndCostOptions?.filter((x) => x.type === 'embold').length
			) {
				return contractTypeRedirects()
			}
			// Check is user have findCareAndCostOptions
			else if (
				to.meta.rightsValue.includes('findCareAndCostOptions') &&
				rightsStore.findCareAndCostOptions?.length
			) {
				return contractTypeRedirects()
			}
			// Check all other rights
			else if (hasRights()) {
				return contractTypeRedirects()
			}
			// IF no access 404
			else {
				return 'NotFound'
			}
		}
		// IF page doesn't need rights
		else {
			if (to.meta?.contractTypes) {
				if ((to.meta?.contractTypes as string[]).includes(planStore.contractType.toLowerCase())) {
					return contractTypeRedirects()
				} else {
					return 'NotFound'
				}
			} else {
				return contractTypeRedirects()
			}
		}
	}

	function updatePageTitle() {
		if (to.meta?.title) {
			document.title = `${to.meta.title} - Blueprint Portal`
		} else if (to.meta?.pageTitle) {
			document.title = `${getContent(to.params.id).title} - Blueprint Portal`
		} else {
			document.title = `Blueprint Portal`
		}
	}
	updatePageTitle()

	document.addEventListener('visibilitychange', () => {
		updatePageTitle()
	})

	const docBody = document.querySelector('body')

	function removeChat() {
		if (document.querySelector('#five9LiveChatWidget'))
			document.querySelector('#five9LiveChatWidget')?.remove()
		if (document.querySelector('#five9OpenChatButton'))
			document.querySelector('#five9OpenChatButton')?.remove()
	}

	const removeListener = () => {
		if (docBody?.classList.contains('chat_started')) {
			if (docBody.classList.contains('no_chat')) removeChat()
			docBody.classList.remove('chat_started')
		}
	}

	// Added to remove the widget from any page that is in ban list
	if (Object.values(banList).filter((subRoute) => subRoute.path === to.path).length > 0) {
		docBody?.classList.add('no_chat')
		window.addEventListener('chatEnded', removeListener, { once: true })

		if (docBody?.classList.contains('chat_started')) {
			window.Five9ChatTerminate()
		} else {
			removeChat()
		}
	} else {
		docBody?.classList.remove('no_chat')
	}

	let token = sessionStorage.getItem('access_token')
	if (to.path == '/terms-of-use' && (token == null || token == undefined || token == 'undefined')) {
		to.meta.hideNavigation = true
		to.meta.hideFooter = true
		to.meta.hideUntilPlanSelected = true
	} else if (
		to.path == '/terms-of-use' ||
		to.path == '/medical/terms-of-use' ||
		to.path == '/dental/terms-of-use' ||
		to.path == '/vision/terms-of-use'
	) {
		to.meta.requiresAuth = true
	}
	if (to.path == '/auth-success') {
		return true
	} else if (to.meta.requiresAuth) {
		if (token == null || token == undefined || token == 'undefined') {
			sessionStorage.setItem('deep_link', to.fullPath)
			const urlParams = new URLSearchParams(window.location.search)
			if (to.path.includes('/messages') && urlParams.get('messageId')) {
				sessionStorage.setItem('deepLinkMessage', urlParams.get('messageId') ?? '')
			} else if (
				(to.path.includes('/message_center') || to.path.includes('/messages')) &&
				to.query.messageId
			) {
				sessionStorage.setItem('deepLinkMessage', to.query.messageId as string)
			}
			return { name: 'login' }
		} else if (!isTokenValid()) {
			await refreshAuthToken()
			return routeAccessRights()
		} else {
			return routeAccessRights()
		}
	} else {
		if (to.name === 'NotFound' && token != null && useSelectedPlanStore().loaded) {
			to.meta.hideNavigation = false
			to.meta.hideFooter = false
			to.meta.hideUntilPlanSelected = false
		} else if (to.name === 'NotFound' && token != null) {
			to.meta.hideNavigation = false
		}

		return true
	}
})

const checkSystemAvailable = async (path: string) => {
	const res = await getSystemAvailability()
	const maintenanceMode = import.meta.env.VITE_MAINTENANCE_MODE.toLowerCase()

	if (
		(maintenanceMode === 'true' || !res.isSystemAvailable) &&
		localStorage.getItem('bypass_outage') != 'true'
	) {
		router.push({ name: 'outagemsg' })
	} else if (path == '/bypass-outage') {
		router.push({ name: 'login' })
	}
}

const getToken = async (code: string) => {
	await getOAuthToken(code).then(() => {
		const token = sessionStorage.getItem('access_token')
		return
	})
}

export default router
